import type {
  ComponentType,
  ErrorInfo,
  ForwardedRef,
  PropsWithChildren,
} from "react";
import { Component, forwardRef } from "react";

import { useReportFrontendErrorMutation } from "metabase/api";
import { SmallGenericError } from "metabase/common/components/ErrorPages";

/**
 * Props the boundary may pass to a custom fallback. Fallbacks are free to
 * ignore them, which is why they are all optional: every existing caller
 * passes a zero-prop component.
 */
interface ErrorFallbackProps {
  message?: string;
  "data-testid"?: string;
  ref?: ForwardedRef<HTMLDivElement>;
}

interface ErrorBoundaryProps extends PropsWithChildren {
  onError?: (errorInfo: ErrorInfo) => void;
  errorComponent?: ComponentType;
  message?: string;
}

interface ErrorBoundaryInnerProps extends ErrorBoundaryProps {
  forwardedRef?: ForwardedRef<HTMLDivElement>;
  onReportError: (error: Error) => void;
}

interface ErrorBoundaryInnerState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundaryInner extends Component<
  ErrorBoundaryInnerProps,
  ErrorBoundaryInnerState
> {
  constructor(props: any) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.props.onReportError(error);
    // if we don't provide a specific onError action, the component will display a generic error message
    if (this.props.onError) {
      this.props.onError(errorInfo);
      this.setState({
        hasError: false,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.errorComponent) {
        // Callers register zero-prop fallbacks, but the boundary always passes
        // a ref, message and test id. Narrowing here keeps those call sites
        // valid without widening the public `errorComponent` prop type.
        const ErrorComponent = this.props
          .errorComponent as ComponentType<ErrorFallbackProps>;
        return (
          <ErrorComponent
            ref={this.props.forwardedRef}
            message={this.props.message}
            data-testid="error-boundary"
          />
        );
      }
      return (
        <div
          ref={this.props.forwardedRef}
          style={{
            padding: "20px",
            margin: "16px 0",
            backgroundColor: "rgba(239, 68, 68, 0.08)",
            border: "1px solid rgba(239, 68, 68, 0.4)",
            borderRadius: "8px",
            color: "#fca5a5",
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          }}
          data-testid="error-boundary"
        >
          <div
            style={{
              fontSize: "15px",
              fontWeight: "bold",
              marginBottom: "8px",
              color: "#ef4444",
            }}
          >
            ⚠️ Component Render Error:{" "}
            {this.state.error?.message || "Unknown error"}
          </div>
          {this.state.error?.stack && (
            <pre
              style={{
                fontSize: "12px",
                color: "#e2e8f0",
                backgroundColor: "#09090b",
                padding: "12px",
                borderRadius: "6px",
                overflow: "auto",
                maxHeight: "260px",
                whiteSpace: "pre-wrap",
                margin: 0,
              }}
            >
              {this.state.error.stack}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// eslint-disable-next-line import/no-default-export -- deprecated usage
export default forwardRef<HTMLDivElement, ErrorBoundaryProps>(
  function ErrorBoundary(props, ref) {
    const [reportFrontendError] = useReportFrontendErrorMutation();
    return (
      <ErrorBoundaryInner
        {...props}
        forwardedRef={ref}
        onReportError={(err) =>
          void reportFrontendError({
            type: "component-crash",
            message: err?.message,
            stack: err?.stack,
          })
        }
      />
    );
  },
);
